import React, { useState, useEffect } from "react";
import Modal from "@material-ui/core/Modal";
import { PrimaryButton } from "../Buttons";
import { toast } from "react-toastify";
import {
  clearCreateWithdrawSuccess,
  createWithdrawRequest,
} from "../../redux/action";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { Link } from "react-router-dom/cjs/react-router-dom.min";
// Icons

import useCurrency from "../../hooks/useCurrency";
import CurrencyDisplay from "../Currency/CurrencyDisplay";

// Icons
import {
  FaUniversity,
  FaBitcoin,
  FaWallet,
  FaInfoCircle,
  FaExclamationTriangle,
  FaTimes,
  FaMobile,
  FaCopy,
  FaPaperPlane,
} from "react-icons/fa";
import { ArrowBack } from "@mui/icons-material";

const Withdraw = ({ isHome }) => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);

  const handleClose = () => setOpen(false);

  const dispatch = useDispatch();
  const {
    getCurrencySymbol,
    convertUSDToDisplay,
    convertDisplayToUSD,
    isLoaded,
  } = useCurrency();

  // Local state for UI logic
  const [withdrawalOption, setWithdrawalOption] = useState("airtel");
  const [amount, setAmount] = useState(""); // Keep as string for better input handling
  const [error, setError] = useState("");

  const {
    user,
    createWithdrawLoading,
    createWithdrawSuccess,
    createWithdrawError,
  } = useSelector((state) => ({
    user: state.userData.user,
    createWithdrawLoading: state.loadingIndicator.createWithdrawLoading,
    createWithdrawSuccess: state.ajaxStatuses.success.createWithdraw,
    createWithdrawError: state.ajaxStatuses.errors.createWithdraw,
  }));

  // --- Logic Helpers ---

  const validateAmount = (val) => {
    if (!isLoaded) return "Loading exchange rates...";

    const numVal = Number(val);
    if (val === "") return ""; // Allow empty
    if (numVal < 0) return "Enter a positive number";

    // Convert minimum withdrawal to display currency
    const minWithdrawalDisplay = convertUSDToDisplay(10);
    const currencySymbol = getCurrencySymbol();

    if (numVal < minWithdrawalDisplay) {
      return `Minimum withdrawal is ${currencySymbol}${minWithdrawalDisplay.toFixed(
        2,
      )}`;
    }

    // Convert user balance to display currency for comparison
    const balanceInDisplayCurrency = convertUSDToDisplay(user.balance);
    if (numVal > balanceInDisplayCurrency) return "Insufficient balance";

    return "";
  };

  const handleAmountChange = (e) => {
    const val = e.target.value;
    setAmount(val);

    const validationError = validateAmount(val);
    setError(validationError);

    // Convert display currency to USD for API
    if (val && !validationError && isLoaded) {
      const usdValue = convertDisplayToUSD(Number(val));
      formik.setFieldValue("amount", usdValue);
    } else {
      formik.setFieldValue("amount", 0);
    }
  };

  const handleSetPercentage = (percent) => {
    if (!user.balance || !isLoaded) return;

    // Convert user balance to display currency
    const balanceInDisplayCurrency = convertUSDToDisplay(user.balance);
    const newAmount = (balanceInDisplayCurrency * percent).toFixed(2);

    setAmount(newAmount);

    // Convert back to USD for API
    const usdValue = convertDisplayToUSD(Number(newAmount));
    formik.setFieldValue("amount", usdValue);

    setError(""); // Clear errors on auto-fill
  };

  const formik = useFormik({
    initialValues: {
      type: "Withdrawal",
      amount: "",
      withdrawalOption: "bank",
    },
    onSubmit: (values) => {
      const payload = {
        ...values,
        user: user._id,
        email: user.email,
        userName: user.userName,
        withdrawalOption: withdrawalOption,
      };

      if (!error && amount && !validateAmount(amount) && isLoaded) {
        dispatch(createWithdrawRequest(payload));
      } else if (!amount) {
        setError("Please enter an amount");
      } else if (!isLoaded) {
        setError("Please wait for exchange rates to load");
      }
    },
  });

  // --- Effects ---

  useEffect(() => {
    if (createWithdrawError) toast.error(createWithdrawError);
  }, [createWithdrawError]);

  useEffect(() => {
    if (createWithdrawSuccess) {
      toast.success(createWithdrawSuccess);
      dispatch(clearCreateWithdrawSuccess());
      setAmount(""); // Reset form
    }
  }, [createWithdrawSuccess, dispatch]);

  const MethodCard = ({
    icon: Icon,
    label,
    subLabel,
    isActive,
    onClick,
    iconBg,
  }) => (
    <div
      onClick={onClick}
      className={`
      cursor-pointer flex items-center px-3 py-3 rounded-xl border-2 transition-all duration-200 mb-3
      ${isActive ? "border-[#1a4bdf] bg-white shadow-md" : "border-gray-100 bg-white hover:border-blue-100"}
    `}
    >
      <div
        className={`w-11 h-11 rounded-xl flex items-center justify-center mr-4 text-lg ${iconBg || "bg-[#f0f4ff] text-[#1a4bdf]"}`}
      >
        <Icon />
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-[15px] text-slate-900">{label}</h3>
        <p className="text-[11px] text-slate-500 font-medium">{subLabel}</p>
      </div>
      {/* Radio Indicator */}
      <div
        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${isActive ? "border-[#1a4bdf]" : "border-gray-200"}`}
      >
        {isActive && <div className="w-2.5 h-2.5 bg-[#1a4bdf] rounded-full" />}
      </div>
    </div>
  );

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        className="flex justify-center outline-0 transition duration-500 "
      >
        <div className="w-[428px] h-screen bg-[#f8faff] overflow-y-auto pb-40 relative shadow-2xl">
          {/* --- Top Navigation --- */}
          <div className="sticky top-0 z-30 bg-[#f8faff]/80 backdrop-blur-md px-4 py-4 flex items-center justify-between">
            <button
              onClick={handleClose}
              className="flex items-center gap-2 bg-white px-4 py-2 rounded-full border border-gray-100 shadow-sm text-[#1a4bdf] font-semibold text-[13px] active:scale-95 transition-transform"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
              Back
            </button>
            <h2 className="text-[17px] font-semibold text-slate-900 pr-10">
              Withdraw Funds
            </h2>
            <div /> {/* Spacer */}
          </div>

          {/* --- Balance Header Card --- */}
          <div className="px-4 mt-2">
            <div
              className="relative h-[100px] bg-gradient-to-br from-[#0642cc] to-[#041a63] rounded-xl p-4 pb-6 overflow-hidden shadow-lg shadow-blue-900/20 bg-right-top bg-no-repeat"
              style={{
                backgroundImage: "url('/img/new/background/shieldcoin.jpeg')",
                backgroundSize: "cover",
              }}
            >
              <div className="relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-blue-100 text-[13px] font-medium">
                    Available Balance
                  </p>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="opacity-80"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                </div>
                <h1 className="text-[32px] font-semibold text-white tracking-tight">
                  <CurrencyDisplay usdAmount={user.balance} />
                </h1>
              </div>
            </div>
          </div>

          <div className="px-4 mt-8">
            {/* --- 1. Method Selection --- */}
            <div className="mb-8">
              <label className="block text-[13px] font-semibold text-slate-900 mb-4 ml-1">
                Select Destination
              </label>
              <MethodCard
                icon={FaUniversity}
                label="Bank Transfer"
                subLabel="Instant Transfer"
                isActive={withdrawalOption === "bank"}
                onClick={() => setWithdrawalOption("bank")}
              />
              <MethodCard
                icon={FaMobile}
                label="Airtel Money"
                subLabel="Instant Transfer"
                isActive={withdrawalOption === "airtel"}
                onClick={() => setWithdrawalOption("airtel")}
                iconBg="bg-[#eff6ff] text-blue-600"
              />
              <MethodCard
                icon={FaMobile}
                label="MTN Money"
                subLabel="Instant Transfer"
                isActive={withdrawalOption === "mtn"}
                onClick={() => setWithdrawalOption("mtn")}
                iconBg="bg-[#fefce8] text-yellow-600"
              />
              <MethodCard
                icon={FaBitcoin}
                label="Crypto Wallet"
                subLabel="Instant Transfer"
                isActive={withdrawalOption === "crypto"}
                onClick={() => setWithdrawalOption("crypto")}
                iconBg="bg-[#f5f3ff] text-purple-600"
              />
            </div>

            {/* --- 2. Amount Input --- */}
            <div className="mb-8">
              <label className="block text-[13px] font-semibold text-slate-900 mb-4 ml-1">
                Withdrawal Amount
              </label>
              <div className="relative bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden flex items-center p-1">
                <div className="w-12 h-12 flex items-center justify-center text-slate-400 font-semibold text-[18px] border-r border-gray-50">
                  {getCurrencySymbol ? getCurrencySymbol() : "K"}
                </div>
                {/* text-[16px] to prevent Safari Zoom */}
                <input
                  type="number"
                  placeholder="0.00"
                  value={amount}
                  onChange={handleAmountChange}
                  className="flex-1 py-4 px-4 text-[18px] font-semibold text-slate-800 placeholder-slate-300 outline-none"
                />
                <div className="pr-5 text-[12px] font-semibold text-[#1a4bdf] tracking-widest">
                  ZMW
                </div>
              </div>

              {/* Quick Percent Buttons */}
              <div className="grid grid-cols-3 gap-3 mt-4">
                {["25%", "50%", "Max"].map((label, idx) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => handleSetPercentage([0.25, 0.5, 1][idx])}
                    className="py-3.5 bg-white border border-gray-100 rounded-xl text-[13px] font-semibold text-[#1a4bdf] shadow-sm active:bg-blue-50 transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>

              {error && (
                <div className="mt-3 flex items-center gap-2 text-red-500 text-[12px] font-medium bg-red-50 p-3 rounded-xl border border-red-100">
                  <FaInfoCircle /> {error}
                </div>
              )}
            </div>

            {/* --- 3. WAC / Details Cards --- */}
            <div className="space-y-4 mb-4">
              {/* WAC Card */}
              <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#f0fdf4] text-[#10b981] rounded-xl flex items-center justify-center border border-[#dcfce7]">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      <polyline points="9 12 11 14 15 10"></polyline>
                    </svg>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-[11px] text-slate-500 font-medium">
                      Required for Withdrawal
                    </span>
                    <span className="text-[13px] font-semibold text-slate-800 uppercase">
                      WAC
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {user.wac ? (
                    <span className="text-[13px] font-semibold text-[#10b981] font-mono">
                      {user.wac || "Not Set"}
                    </span>
                  ) : (
                    <div className="flex justify-between items-center">
                      <Link
                        to="/dashboard/profile"
                        className="flex items-center gap-1 text-xs text-red-500 font-semibold hover:underline"
                      >
                        <FaExclamationTriangle /> Set WAC Code
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 4. Destination Details Card */}
            <div className="mb-8">
              {withdrawalOption === "airtel" && (
                <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#eff6ff] text-blue-600 rounded-xl flex items-center justify-center border border-blue-100">
                      <FaMobile className="text-[18px]" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] text-slate-500 font-medium">
                        Destination
                      </span>
                      <span className="text-[13px] font-semibold text-slate-800">
                        Airtel Money
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {user.airtelMoneyAccount ? (
                      <span className="text-[13px] font-semibold text-blue-600 font-mono">
                        {user.airtelMoneyAccount}
                      </span>
                    ) : (
                      <Link
                        to="/dashboard/profile"
                        className="flex items-center gap-1 text-xs text-red-500 font-semibold hover:underline"
                      >
                        <FaExclamationTriangle /> Set Account
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {withdrawalOption === "mtn" && (
                <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#fefce8] text-yellow-600 rounded-xl flex items-center justify-center border border-yellow-100">
                      <FaMobile className="text-[18px]" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] text-slate-500 font-medium">
                        Destination
                      </span>
                      <span className="text-[13px] font-semibold text-slate-800">
                        MTN Money
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {user.mtnMoneyAccount ? (
                      <span className="text-[13px] font-semibold text-yellow-600 font-mono">
                        {user.mtnMoneyAccount}
                      </span>
                    ) : (
                      <Link
                        to="/dashboard/profile"
                        className="flex items-center gap-1 text-xs text-red-500 font-semibold hover:underline"
                      >
                        <FaExclamationTriangle /> Set Account
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {withdrawalOption === "bank" && (
                <div className="space-y-3">
                  <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#f0fdf4] text-[#10b981] rounded-xl flex items-center justify-center border border-[#dcfce7]">
                        <FaUniversity className="text-[18px]" />
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11px] text-slate-500 font-medium">
                          Bank Name
                        </span>
                        <span className="text-[13px] font-semibold text-slate-800">
                          {user.bankName || "Not Set"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {!user.bankName && (
                        <Link
                          to="/dashboard/profile"
                          className="flex items-center gap-1 text-xs text-red-500 font-semibold hover:underline"
                        >
                          <FaExclamationTriangle /> Set Bank
                        </Link>
                      )}
                    </div>
                  </div>

                  <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#f0fdf4] text-[#10b981] rounded-xl flex items-center justify-center border border-[#dcfce7]">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="4" y1="9" x2="20" y2="9"></line>
                          <line x1="4" y1="15" x2="20" y2="15"></line>
                          <line x1="10" y1="3" x2="8" y2="21"></line>
                          <line x1="16" y1="3" x2="14" y2="21"></line>
                        </svg>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11px] text-slate-500 font-medium">
                          Account Number
                        </span>
                        <span className="text-[13px] font-semibold text-slate-800">
                          {user.accountNumber || "Not Set"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {user.accountNumber ? (
                        <span className="text-[13px] font-semibold text-[#10b981] font-mono">
                          {user.accountNumber}
                        </span>
                      ) : (
                        <Link
                          to="/dashboard/profile"
                          className="flex items-center gap-1 text-xs text-red-500 font-semibold hover:underline"
                        >
                          <FaExclamationTriangle /> Set Account
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {withdrawalOption === "crypto" && (
                <div className="bg-white border border-gray-100 rounded-2xl p-4 flex items-center justify-between shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#f5f3ff] text-purple-600 rounded-xl flex items-center justify-center border border-purple-100">
                      <FaBitcoin className="text-[18px]" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[11px] text-slate-500 font-medium">
                        Destination
                      </span>
                      <span className="text-[13px] font-semibold text-slate-800">
                        Crypto Wallet
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {user.walletAddress ? (
                      <span className="text-[13px] font-semibold text-purple-600 font-mono">
                        {user.walletAddress.slice(0, 6)}...
                        {user.walletAddress.slice(-4)}
                      </span>
                    ) : (
                      <Link
                        to="/dashboard/profile"
                        className="flex items-center gap-1 text-xs text-red-500 font-semibold hover:underline"
                      >
                        <FaExclamationTriangle /> Set Wallet
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* --- 4. Withdraw Button --- */}
            <button
              onClick={formik?.handleSubmit}
              disabled={
                !amount ||
                !!error ||
                Number(amount) <= 0 ||
                !isLoaded ||
                !user.wac ||
                (withdrawalOption === "crypto" && !user.walletAddress) ||
                (withdrawalOption === "airtel" && !user.airtelMoneyAccount) ||
                (withdrawalOption === "mtn" && !user.mtnMoneyAccount) ||
                (withdrawalOption === "bank" &&
                  (!user.accountNumber || !user.bankName))
              }
              className="w-full bg-[#1a4bdf] hover:bg-blue-700 disabled:bg-[#a5bcf6] text-white rounded-xl py-4 flex items-center justify-center gap-3 shadow-lg shadow-blue-600/20 active:scale-[0.98] transition-all"
            >
              {createWithdrawLoading ? (
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                <FaPaperPlane className="text-[14px]" />
              )}
              <span className="text-[16px] font-semibold">Withdraw</span>
            </button>
          </div>
        </div>
      </Modal>

      <div onClick={handleOpen}>
        {/* Withdraw Button */}
        <div className="bg-white rounded-[1.25rem] py-3 px-2 flex flex-col items-center text-center  cursor-pointer hover:bg-blue-50/50 transition-colors active:scale-95">
          <div className="w-12 h-12 rounded-full bg-[#f0f4ff] flex items-center justify-center text-[#1a4bdf] mb-3">
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="17 8 12 3 7 8"></polyline>
              <line x1="12" y1="3" x2="12" y2="15"></line>
            </svg>
          </div>
          <h4 className="text-[13px] font-semibold text-slate-800 mb-1">
            Withdraw
          </h4>
          <p className="text-[10px] text-slate-500 font-medium leading-tight">
            Cash out
          </p>
        </div>
      </div>
    </>
  );
};

export default Withdraw;
